import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';

const MultiOrgSelector = ({ organizations = [], selectedOrgs = [], onChange }) => {
  const [organizationAssociations, setOrganizationAssociations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());

  useEffect(() => {
    // Initialize from existing selectedOrgs (for edit mode)
    if (selectedOrgs && selectedOrgs.length > 0) {
      const associations = selectedOrgs.map(org => ({
        organization_id: org.id || org.organization_id,
        organization_name: org.name || organizations.find(o => o.id === (org.id || org.organization_id))?.detail?.org_name || 'Unknown',
        user_id_access_list: org.users ? org.users.map(u => u.id) : (org.user_id_access_list || []),
        association_id: org.association_id
      }));
      setOrganizationAssociations(associations);
      
      // Load users for each selected org
      associations.forEach(assoc => {
        loadUsersForOrganization(assoc.organization_id);
      });
    }
  }, [selectedOrgs]);

  const loadUsersForOrganization = async (organizationId) => {
    if (!organizationId || availableUsers[organizationId]) {
      return;
    }

    setLoadingUsers(prev => ({ ...prev, [organizationId]: true }));
    
    try {
      const users = await apiClient.get(`/organizations/${organizationId}/users`);
      setAvailableUsers(prev => ({ ...prev, [organizationId]: users || [] }));
    } catch (err) {
      console.error('Error loading users for organization:', err);
      setAvailableUsers(prev => ({ ...prev, [organizationId]: [] }));
    } finally {
      setLoadingUsers(prev => ({ ...prev, [organizationId]: false }));
    }
  };

  const handleAddOrganization = (organizationId) => {
    if (!organizationId) return;
    
    const orgId = parseInt(organizationId);
    const org = organizations.find(o => o.id === orgId);
    
    if (!org) return;
    
    // Check if already added
    if (organizationAssociations.some(a => a.organization_id === orgId)) {
      alert('This organization is already added');
      return;
    }
    
    const newAssociation = {
      organization_id: orgId,
      organization_name: org.detail?.org_name || `Organization ${orgId}`,
      user_id_access_list: [] // Start with empty list (means all users have access)
    };
    
    const updatedAssociations = [...organizationAssociations, newAssociation];
    setOrganizationAssociations(updatedAssociations);
    setExpandedOrgs(prev => new Set([...prev, orgId]));
    
    // Load users for this org
    loadUsersForOrganization(orgId);
    
    // Notify parent
    onChange(updatedAssociations);
  };

  const handleRemoveOrganization = (organizationId) => {
    const updatedAssociations = organizationAssociations.filter(
      a => a.organization_id !== organizationId
    );
    setOrganizationAssociations(updatedAssociations);
    onChange(updatedAssociations);
  };

  const handleUserToggle = (organizationId, userId) => {
    const updatedAssociations = organizationAssociations.map(assoc => {
      if (assoc.organization_id === organizationId) {
        const currentUsers = assoc.user_id_access_list || [];
        const newUsers = currentUsers.includes(userId)
          ? currentUsers.filter(id => id !== userId)
          : [...currentUsers, userId];
        
        return {
          ...assoc,
          user_id_access_list: newUsers
        };
      }
      return assoc;
    });
    
    setOrganizationAssociations(updatedAssociations);
    onChange(updatedAssociations);
  };

  const handleSelectAllUsers = (organizationId, selectAll) => {
    const users = availableUsers[organizationId] || [];
    
    const updatedAssociations = organizationAssociations.map(assoc => {
      if (assoc.organization_id === organizationId) {
        return {
          ...assoc,
          user_id_access_list: selectAll ? users.map(u => u.id) : []
        };
      }
      return assoc;
    });
    
    setOrganizationAssociations(updatedAssociations);
    onChange(updatedAssociations);
  };

  const toggleOrgExpansion = (orgId) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  // Get organizations that haven't been added yet
  const availableOrganizations = organizations.filter(
    org => !organizationAssociations.some(a => a.organization_id === org.id)
  );

  return (
    <div className="multi-org-selector">
      <div className="mb-3">
        <label className="form-label">Add Organization Access</label>
        <div className="input-group">
          <select 
            className="form-control"
            onChange={(e) => {
              handleAddOrganization(e.target.value);
              e.target.value = ''; // Reset selection
            }}
            value=""
          >
            <option value="">Select Organization to Add</option>
            {availableOrganizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.detail?.org_name || `Organization ${org.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {organizationAssociations.length > 0 && (
        <div className="selected-organizations">
          <h6 className="mb-2">Selected Organizations ({organizationAssociations.length})</h6>
          
          {organizationAssociations.map(assoc => {
            const users = availableUsers[assoc.organization_id] || [];
            const isExpanded = expandedOrgs.has(assoc.organization_id);
            const selectedUserCount = assoc.user_id_access_list?.length || 0;
            
            return (
              <div key={assoc.organization_id} className="card mb-2">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 me-2"
                      onClick={() => toggleOrgExpansion(assoc.organization_id)}
                    >
                      <i className={`ti-angle-${isExpanded ? 'down' : 'right'}`}></i>
                    </button>
                    <strong>{assoc.organization_name}</strong>
                    <span className="badge bg-info ms-2">
                      {selectedUserCount === 0 
                        ? 'Full Access' 
                        : `${selectedUserCount} user${selectedUserCount !== 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => handleRemoveOrganization(assoc.organization_id)}
                  >
                    <i className="ti-trash"></i> Remove
                  </button>
                </div>
                
                {isExpanded && (
                  <div className="card-body">
                    {loadingUsers[assoc.organization_id] ? (
                      <div className="text-center">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading users...</span>
                        </div>
                      </div>
                    ) : users.length > 0 ? (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">User Access</label>
                          <div className="btn-group btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => handleSelectAllUsers(assoc.organization_id, true)}
                            >
                              Select All
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handleSelectAllUsers(assoc.organization_id, false)}
                            >
                              Clear All
                            </button>
                          </div>
                        </div>
                        
                        <div className="alert alert-info py-2 px-3 small">
                          <i className="ti-info-alt me-1"></i>
                          Empty selection means ALL users in this organization have access
                        </div>
                        
                        <div 
                          style={{ 
                            maxHeight: '200px', 
                            overflowY: 'auto', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            padding: '10px' 
                          }}
                        >
                          {users.map(user => (
                            <div key={user.id} className="form-check">
                              <input 
                                className="form-check-input"
                                type="checkbox"
                                id={`org-${assoc.organization_id}-user-${user.id}`}
                                checked={assoc.user_id_access_list.includes(user.id)}
                                onChange={() => handleUserToggle(assoc.organization_id, user.id)}
                              />
                              <label 
                                className="form-check-label" 
                                htmlFor={`org-${assoc.organization_id}-user-${user.id}`}
                              >
                                {user.detail?.user_name || 'Unknown User'} 
                                <span className="text-muted ms-1">
                                  ({user.detail?.email || 'No email'})
                                </span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted">No users found in this organization</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {organizationAssociations.length === 0 && (
        <div className="alert alert-warning">
          No organizations selected. Add at least one organization to provide access.
        </div>
      )}
    </div>
  );
};

export default MultiOrgSelector;